// src/App.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { FiLogOut } from 'react-icons/fi';
import toast, { Toaster } from 'react-hot-toast';
import { Helmet } from 'react-helmet';
import Login from './components/Login/Login';
import Sidebar from './components/app/Sidebar';
import DealerManagement from './components/Dealer';
import UserManagement from './components/User';
import ServerManagement from './components/Server';
import Permissions from './components/Permission';
import EditUserPermissions from './components/Permission/EditUserPermissions';
import AssetManagement from './components/Asset';
import BundleManagement from './components/Bundle';
import DeviceManagement from './components/Device';
import EditUserDevices from './components/Device/EditUserDevices';
import APITest from './components/APITest';
import Statistics from './components/Statistics';
import DataAnalytics from './components/DataAnalytics';
import { setLogoutCallback } from './services/api';
import './App.scss';

const MemoizedPermissions = React.memo(Permissions);

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [title, setTitle] = useState('EarthVerse CRM');
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const email = localStorage.getItem('user_email');
    if (token) {
      setIsAuthenticated(true);
      setUserEmail(email || '');
    }
  }, []);

  const handleLogin = useCallback((email) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Invalid token. Please log in again.');
      return;
    }
    setIsAuthenticated(true);
    setUserEmail(email);
    localStorage.setItem('user_email', email);
    toast.success('Logged in successfully!');
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user_email');
    setIsAuthenticated(false);
    setUserEmail('');
    setTitle('EarthVerse CRM');
    navigate('/');
    toast.success('Logged out successfully!');
  }, [navigate]);

  useEffect(() => {
    setLogoutCallback(handleLogout);
  }, [handleLogout]);

  const updateTitle = useCallback((newTitle) => {
    setTitle(userEmail ? `${newTitle} (${userEmail})` : newTitle);
  }, [userEmail]);

  const memoizedUpdateTitle = useMemo(() => updateTitle, [updateTitle]);

  if (!isAuthenticated) {
    return (
      <div className="login-container">
        <Login onLogin={handleLogin} />
      </div>
    );
  }

  return (
    <>
      <Sidebar />
      <div className="main-content">
        <Routes>
          <Route path="/servers" element={<ServerManagement setTitle={memoizedUpdateTitle} />} />
          <Route path="/dealers" element={<DealerManagement setTitle={memoizedUpdateTitle} />} />
          <Route path="/users" element={<UserManagement setTitle={memoizedUpdateTitle} />} />
          <Route path="/devices" element={<DeviceManagement setTitle={memoizedUpdateTitle} />} />
          <Route path="/devices/:userId" element={<EditUserDevices setTitle={memoizedUpdateTitle} />} />
          <Route path="/assets" element={<AssetManagement setTitle={memoizedUpdateTitle} />} />
          <Route path="/bundles" element={<BundleManagement setTitle={memoizedUpdateTitle} />} />
          <Route path="/permissions" element={<MemoizedPermissions setTitle={memoizedUpdateTitle} />} />
          <Route path="/permissions/:userId/edit" element={<EditUserPermissions setTitle={memoizedUpdateTitle} />} />
          <Route path="/statistics" element={<Statistics setTitle={memoizedUpdateTitle} />} />
          <Route path="/data" element={<DataAnalytics setTitle={memoizedUpdateTitle} />} />
          <Route path="/api-test" element={<APITest setTitle={memoizedUpdateTitle} />} />
          <Route path="/" element={<Navigate to="/users" replace />} />
        </Routes>
        <button className="floating-logout-btn" onClick={handleLogout} title="Logout">
          <FiLogOut size={24} />
        </button>
      </div>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <Helmet>
        <title>EarthVerse CRM</title>
      </Helmet>
      <Toaster position="top-right" />
      <Router>
        <AppContent />
      </Router>
    </div>
  );
}

export default App;